import React, { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import moment from "moment";
import DatePicker from "react-multi-date-picker";

import { airportsService } from "../../services/airports.service";

const toArray = (rd) => (Array.isArray(rd) ? rd : rd?.data || []);

const CABIN_CLASSES = ["Economy", "Premium Economy", "Business", "First"];

// Shown like the design; the fare-search API has no field for these, so they are not sent
const SPECIAL_FARES = [
  { value: "student", label: "Student" },
  { value: "senior_citizen", label: "Senior Citizen" },
  { value: "armed_forces", label: "Armed Forces" },
];

const DEFAULT_FROM = {
  value: "DEL", airport_code: "DEL", city_name: "Delhi",
  airport_name: "Indira Gandhi International Airport", country_name: "India",
};
const DEFAULT_TO = {
  value: "BOM", airport_code: "BOM", city_name: "Mumbai",
  airport_name: "Chhatrapati Shivaji Maharaj International Airport", country_name: "India",
};

const MAX_PAX = 9;
const ACCENT = "#4f3df5";

const airportSelectStyles = {
  control: (base) => ({ ...base, border: "none", boxShadow: "none", minHeight: 36, fontSize: 16, fontWeight: 600, background: "transparent" }),
  valueContainer: (base) => ({ ...base, padding: 0 }),
  indicatorsContainer: () => ({ display: "none" }),
  menu: (base) => ({ ...base, minWidth: 340, borderRadius: 10, overflow: "hidden" }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  option: (base, state) => ({
    ...base, fontSize: 13, cursor: "pointer",
    background: state.isSelected ? ACCENT : state.isFocused ? "#eef0ff" : "#fff",
    color: state.isSelected ? "#fff" : "#1f2937",
  }),
};

const formatAirportOption = (o, { context }) =>
  context == "menu" ? (
    <div className="d-flex justify-content-between align-items-center gap-3">
      <div>
        <div style={{ fontWeight: 600 }}>{o.city_name}, {o.country_name}</div>
        <div style={{ fontSize: 11, opacity: 0.75 }}>{o.airport_name}</div>
      </div>
      <span style={{ fontWeight: 700, fontSize: 12 }}>{o.airport_code}</span>
    </div>
  ) : (
    o.city_name
  );

const FlightSearchForm = ({ onSearch, loading }) => {
  const [tripType, setTripType] = useState("one_way");
  const [specialRoundTrip, setSpecialRoundTrip] = useState(false);
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo] = useState(DEFAULT_TO);
  const [editing, setEditing] = useState(null); // "from" | "to" | null
  const [departureDate, setDepartureDate] = useState(moment().add(1, "day").format("YYYY-MM-DD"));
  const [returnDate, setReturnDate] = useState("");
  const [pax, setPax] = useState({ adults: 1, children: 0, infants: 0 });
  const [cabinClass, setCabinClass] = useState("Economy");
  const [paxOpen, setPaxOpen] = useState(false);
  const [specialFare, setSpecialFare] = useState(null);
  const [nonStop, setNonStop] = useState(false);

  const departureRef = useRef(null);
  const returnRef = useRef(null);
  const paxRef = useRef(null);

  const today = moment().format("YYYY-MM-DD");
  const isRoundTrip = tripType == "round_trip";
  const travellers = pax.adults + pax.children + pax.infants;

  // Close the passengers popover on an outside click
  useEffect(() => {
    if (!paxOpen) return;
    const handler = (e) => {
      if (paxRef.current && !paxRef.current.contains(e.target)) setPaxOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [paxOpen]);

  // From / To dropdowns: airports > list (status = active), searched on the server
  const loadAirports = async (search = "") => {
    try {
      const res = await airportsService.list({ page: 1, limit: 50, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return toArray(res.resData).map((a) => ({
          value: a.airport_code,
          airport_code: a.airport_code,
          airport_name: a.airport_name,
          city_name: a.city_name,
          country_name: a.country_name,
        }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      return [];
    }
  };

  const openPicker = (ref) => {
    ref.current?.openCalendar();
  };

  const handleTripType = (type) => {
    setTripType(type);
    if (type == "one_way") {
      setReturnDate("");
      setSpecialRoundTrip(false);
    }
  };

  const handleSpecialRoundTrip = () => {
    const next = !specialRoundTrip;
    setSpecialRoundTrip(next);
    if (next) setTripType("round_trip");
  };

  const handleDepartureChange = (value) => {
    setDepartureDate(value);
    if (returnDate && value && moment(returnDate).isBefore(value)) setReturnDate(value);
  };

  const handleReturnChange = (value) => {
    setReturnDate(value);
    if (value) setTripType("round_trip");
  };

  const handleSwap = () => {
    setFrom(to);
    setTo(from);
  };

  // Adults + children are capped at 9, and each infant needs an adult
  const changePax = (key, delta) => {
    setPax((prev) => {
      const next = { ...prev, [key]: prev[key] + delta };
      if (next.adults < 1 || next.children < 0 || next.infants < 0) return prev;
      if (next.adults + next.children > MAX_PAX) return prev;
      if (next.infants > next.adults) {
        if (key == "infants") return prev;
        next.infants = next.adults;
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!from || !to) return toast.error("Please select both From and To airports");
    if (from.airport_code == to.airport_code) return toast.error("From and To airports cannot be the same");
    if (!departureDate) return toast.error("Please select a departure date");
    if (isRoundTrip && !returnDate) return toast.error("Please select a return date for a round trip");

    const payload = {
      trip_type: tripType,
      route_type: from.country_name && to.country_name && from.country_name != to.country_name ? "international" : "domestic",
      ...(nonStop ? { max_stops: 0 } : {}),
      nearby_airport: true,
      sector: {
        origin: from.airport_code,
        destination: to.airport_code,
        departure_date: departureDate,
        departure_time_from: "00:01",
        departure_time_to: "23:59",
        cabin_class: cabinClass,
        ...(isRoundTrip ? { return_date: returnDate } : {}),
      },
      pax_detail: { ...pax },
    };

    onSearch(payload);
  };

  const renderAirportBox = (key, label, value, setValue) => (
    <div className="fs-box fs-airport" onClick={() => setEditing(key)}>
      <div className="fs-label">{label}</div>
      {editing == key ? (
        <AsyncSelect
          autoFocus
          defaultMenuIsOpen
          cacheOptions
          defaultOptions
          loadOptions={loadAirports}
          value={value}
          onChange={(opt) => { if (opt) setValue(opt); setEditing(null); }}
          onBlur={() => setEditing(null)}
          formatOptionLabel={formatAirportOption}
          getOptionValue={(o) => o.airport_code}
          placeholder="Search city or airport"
          noOptionsMessage={() => "No airports found"}
          styles={airportSelectStyles}
          menuPortalTarget={document.body}
        />
      ) : (
        <div className="fs-value">{value?.city_name || "Select city"}</div>
      )}
      <div className="fs-sub text-truncate">
        {value ? `${value.airport_code}, ${value.airport_name}` : " "}
      </div>
    </div>
  );

  const renderDate = (value) => {
    const m = moment(value);
    return (
      <>
        <div className="fs-value">
          {m.format("D")} <span className="fs-month">{m.format("MMM'YY")}</span>
        </div>
        <div className="fs-sub fs-sub-dark">{m.format("dddd")}</div>
      </>
    );
  };

  const paxRow = (key, title, hint) => (
    <div className="d-flex align-items-center justify-content-between py-2" key={key}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1f2937" }}>{title}</div>
        <div style={{ fontSize: 11, color: "#8a94a6" }}>{hint}</div>
      </div>
      <div className="d-flex align-items-center gap-2">
        <button type="button" className="fs-counter" onClick={() => changePax(key, -1)}>
          <i className="bi bi-dash"></i>
        </button>
        <span style={{ width: 20, textAlign: "center", fontWeight: 700 }}>{pax[key]}</span>
        <button type="button" className="fs-counter" onClick={() => changePax(key, 1)}>
          <i className="bi bi-plus"></i>
        </button>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <style>{formCss}</style>

      {/* Trip type + tagline */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div className="fs-trip-toggle">
          <button type="button" className={`fs-trip ${tripType == "one_way" ? "active" : ""}`} onClick={() => handleTripType("one_way")}>
            One Way
          </button>
          <button type="button" className={`fs-trip ${isRoundTrip ? "active" : ""}`} onClick={() => handleTripType("round_trip")}>
            Round Trip
          </button>
          <span className="fs-divider" />
          <button type="button" className="fs-trip d-flex align-items-center gap-2" onClick={handleSpecialRoundTrip}>
            <span className={`fs-switch ${specialRoundTrip ? "on" : ""}`}><span /></span>
            Special Round Trip
          </button>
        </div>
        <div className="fs-tagline">
          <span className="fs-dot" /> Search Lowest Price
        </div>
      </div>

      {/* Main search bar */}
      <div className="fs-bar">
        <div className="fs-route">
          {renderAirportBox("from", "From", from, setFrom)}
          <button type="button" className="fs-swap" onClick={handleSwap} title="Swap">
            <i className="bi bi-arrow-left-right"></i>
          </button>
          {renderAirportBox("to", "To", to, setTo)}
        </div>

        <div className="fs-box fs-date" onClick={() => openPicker(departureRef)}>
          <div className="fs-label">Departure</div>
          {departureDate ? renderDate(departureDate) : <div className="fs-hint">Select date</div>}
          <div className="fs-hidden-date" onClick={(e) => e.stopPropagation()}>
            <DatePicker
              onOpenPickNewDate={false}
              ref={departureRef}
              value={departureDate || null}
              onChange={(d) => handleDepartureChange(d ? d.format("YYYY-MM-DD") : "")}
              format="YYYY-MM-DD"
              minDate={today}
              editable={false}
              inputClass="fs-hidden-date-input"
            />
          </div>
        </div>

        <div className="fs-box fs-date" onClick={() => openPicker(returnRef)}>
          <div className="fs-label d-flex justify-content-between">
            Return
            {returnDate && (
              <i
                className="bi bi-x-circle-fill"
                style={{ color: "#b0b8c9", cursor: "pointer" }}
                title="Remove return date"
                onClick={(e) => { e.stopPropagation(); handleTripType("one_way"); }}
              />
            )}
          </div>
          {returnDate ? renderDate(returnDate) : <div className="fs-hint">Tap to add a return date for round trip fares</div>}
          <div className="fs-hidden-date" onClick={(e) => e.stopPropagation()}>
            <DatePicker
              onOpenPickNewDate={false}
              ref={returnRef}
              value={returnDate || null}
              onChange={(d) => handleReturnChange(d ? d.format("YYYY-MM-DD") : "")}
              format="YYYY-MM-DD"
              minDate={departureDate || today}
              editable={false}
              inputClass="fs-hidden-date-input"
            />
          </div>
        </div>

        <div className="fs-box fs-pax" ref={paxRef}>
          <div onClick={() => setPaxOpen(!paxOpen)} style={{ cursor: "pointer" }}>
            <div className="fs-label">Passengers &amp; Class</div>
            <div className="d-flex justify-content-between align-items-center">
              <div className="fs-value fs-value-sm">
                {travellers} Traveller{travellers > 1 ? "s" : ""}
              </div>
              <i className={`bi bi-chevron-${paxOpen ? "up" : "down"}`} style={{ color: "#8a94a6", fontSize: 12 }}></i>
            </div>
            <div className="fs-sub">{cabinClass}</div>
          </div>

          {paxOpen && (
            <div className="fs-pax-pop">
              {paxRow("adults", "Adults", "12 yrs & above")}
              {paxRow("children", "Children", "2 - 12 yrs")}
              {paxRow("infants", "Infants", "Below 2 yrs")}
              <div className="fs-label mt-3 mb-2">Travel Class</div>
              <div className="d-flex flex-wrap gap-2">
                {CABIN_CLASSES.map((c) => (
                  <button
                    type="button"
                    key={c}
                    className={`fs-class ${cabinClass == c ? "active" : ""}`}
                    onClick={() => setCabinClass(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="text-end mt-3">
                <button type="button" className="btn btn-sm px-4 fs-done" onClick={() => setPaxOpen(false)}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="fs-search" disabled={loading}>
          {loading ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <>
              SEARCH <i className="bi bi-search"></i>
            </>
          )}
        </button>
      </div>

      {/* Special fares */}
      <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
        <span className="fs-special-title">
          Special Fares <span style={{ fontWeight: 400, opacity: 0.85 }}>(Optional):</span>
        </span>
        {SPECIAL_FARES.map((f) => (
          <button
            type="button"
            key={f.value}
            className={`fs-chip ${specialFare == f.value ? "active" : ""}`}
            onClick={() => setSpecialFare(specialFare == f.value ? null : f.value)}
          >
            <span className="fs-radio" /> {f.label}
          </button>
        ))}
        <button type="button" className={`fs-chip ${nonStop ? "active" : ""}`} onClick={() => setNonStop(!nonStop)}>
          <span className="fs-radio" /> Non Stop
        </button>
      </div>
    </form>
  );
};

const formCss = `
.fs-trip-toggle { display:inline-flex; align-items:center; gap:4px; padding:5px; border-radius:999px;
  background:rgba(255,255,255,0.18); border:1px solid rgba(255,255,255,0.35); backdrop-filter:blur(6px); }
.fs-trip { border:none; background:transparent; color:#fff; font-weight:600; font-size:15px; padding:8px 18px; border-radius:999px; }
.fs-trip.active { background:#fff; color:${ACCENT}; }
.fs-divider { width:1px; height:22px; background:rgba(255,255,255,0.45); margin:0 4px; }
.fs-switch { width:40px; height:22px; border-radius:999px; background:rgba(255,255,255,0.45); position:relative; transition:background .2s; }
.fs-switch span { position:absolute; top:3px; left:3px; width:16px; height:16px; border-radius:50%; background:#fff; transition:left .2s; }
.fs-switch.on { background:${ACCENT}; }
.fs-switch.on span { left:21px; }
.fs-tagline { color:#fff; font-weight:700; font-size:16px; text-shadow:0 1px 4px rgba(0,0,0,.35); display:flex; align-items:center; gap:8px; }
.fs-dot { width:7px; height:7px; border-radius:50%; background:#22c55e; display:inline-block; }

.fs-bar { display:flex; background:#fff; border-radius:12px; border:1px solid #e8ebf7; box-shadow:0 10px 30px rgba(0,0,0,.18); overflow:visible; }
.fs-route { display:flex; flex:2.2; position:relative; min-width:0; }
.fs-box { position:relative; padding:14px 18px; border-right:1px solid #eceff4; min-width:0; cursor:pointer; }
.fs-airport { flex:1; }
.fs-date { flex:1; }
.fs-pax { flex:1.25; cursor:default; }
.fs-label { font-size:11px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#6c757d; margin-bottom:4px; }
.fs-value { font-size:22px; font-weight:700; color:#111827; line-height:1.35; }
.fs-value-sm { font-size:16px; }
.fs-month { font-size:14px; font-weight:600; }
.fs-sub { font-size:13px; color:#6b7280; margin-top:2px; }
.fs-sub-dark { color:#374151; font-size:14px; }
.fs-hint { font-size:13px; font-weight:600; color:${ACCENT}; line-height:1.45; }
.fs-hidden-date { position:absolute; left:18px; bottom:10px; width:1px; height:1px; }
.fs-hidden-date-input { width:1px; height:1px; opacity:0; padding:0; border:none; pointer-events:none; }
.fs-swap { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); z-index:2; width:44px; height:44px; border-radius:50%;
  border:1px solid #c7cdfa; background:#fff; color:${ACCENT}; font-size:16px; display:flex; align-items:center; justify-content:center; }
.fs-swap:hover { background:#eef0ff; }
.fs-search { border:none; background:linear-gradient(135deg, #667eea 0%, #764ba2 100%); color:#fff; font-weight:800; font-size:17px; letter-spacing:1px; padding:0 40px;
  border-radius:0 12px 12px 0; display:flex; align-items:center; justify-content:center; gap:10px; min-width:185px; }
.fs-search:hover:not(:disabled) { filter:brightness(1.08); }
.fs-search:disabled { opacity:.8; }

.fs-pax-pop { position:absolute; top:calc(100% + 8px); right:0; width:320px; background:#fff; border-radius:12px; padding:16px;
  box-shadow:0 12px 32px rgba(0,0,0,.18); z-index:1500; cursor:default; }
.fs-counter { width:30px; height:30px; border-radius:50%; border:1px solid #c7cdfa; background:#fff; color:${ACCENT};
  display:flex; align-items:center; justify-content:center; }
.fs-counter:hover { background:#eef0ff; }
.fs-class { border:1px solid #e5e7eb; background:#fff; border-radius:999px; font-size:12px; padding:5px 12px; color:#374151; }
.fs-class.active { border-color:${ACCENT}; background:#eef0ff; color:${ACCENT}; font-weight:600; }
.fs-done { background:${ACCENT}; color:#fff; border-radius:8px; height:34px; font-size:13px; }
.fs-done:hover { background:#3f2fe0; color:#fff; }

.fs-special-title { color:#fff; font-weight:700; font-size:16px; margin-right:6px; text-shadow:0 1px 4px rgba(0,0,0,.35); }
.fs-chip { display:flex; align-items:center; gap:8px; border:1px solid rgba(255,255,255,.55); background:rgba(0,0,0,.28);
  backdrop-filter:blur(4px); color:#fff; font-weight:600; font-size:14px; padding:8px 18px; border-radius:999px; }
.fs-radio { width:18px; height:18px; border-radius:50%; border:2px solid #fff; display:inline-block; position:relative; }
.fs-chip.active { background:${ACCENT}; border-color:${ACCENT}; }
.fs-chip.active .fs-radio::after { content:""; position:absolute; inset:3px; border-radius:50%; background:#fff; }

@media (max-width: 991px) {
  .fs-bar { flex-wrap:wrap; }
  .fs-route { flex:1 1 100%; border-bottom:1px solid #eceff4; }
  .fs-date, .fs-pax { flex:1 1 33%; border-bottom:1px solid #eceff4; }
  .fs-search { flex:1 1 100%; border-radius:0 0 12px 12px; padding:14px; }
}
@media (max-width: 575px) {
  .fs-route { flex-direction:column; }
  .fs-airport { border-right:none; border-bottom:1px solid #eceff4; }
  .fs-swap { left:auto; right:16px; transform:translateY(-50%) rotate(90deg); }
  .fs-date { flex:1 1 50%; }
  .fs-pax { flex:1 1 100%; }
  .fs-pax-pop { width:100%; }
  .fs-value { font-size:19px; }
}
`;

export default FlightSearchForm;
